import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';
import { DB } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';

import { useToast } from '~/hooks/toast';
import { useCoupons } from '~/hooks/coupons';

interface FormData {
  uid?: string;
  name: string;
  code: string;
  type: number;
  value: number;
  min_value: number;
  max_value: number;
  initDate: string;
  endDate: string;
  usage_limit: number;
  phone_limit: boolean;
  user_id: string;
  company_id: string;
  status: boolean;
}

interface Params {
  id: string;
}

const { REACT_APP_AWS_BUCKET_URL, REACT_APP_FIREBASE_PROJECT } = process.env;

const CouponForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { state } = useLocation();
  const { user, company } = useAuth();
  const { addToast } = useToast();
  const { loadCoupons } = useCoupons();

  const [data, setData] = useState({} as FormData);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const { id } = params;

  useEffect(() => {
    async function loadCoupon() {
      const couponsRef = doc(DB, 'coupons', id);
      const docSnap = await getDoc(couponsRef);
      setData({
        ...docSnap.data(),
        uid: id,
      } as FormData);
    }

    if (state) {
      setData(state as FormData);
    } else if (id) {
      loadCoupon();
    } else {
      setData({ status: true, type: 0 } as FormData);
    }
  }, [id, state]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('O nome da cupom é obrigatório.'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const uid = data.uid || v4();

        const parseData = {
          ...data,
          uid,
          user_id: user.uid,
          company_id: company.uid,
        };

        if (data.uid) {
          await updateDoc(doc(DB, 'coupons', uid), parseData);
        } else {
          await setDoc(doc(DB, 'coupons', uid), parseData);
        }

        loadCoupons(company.uid);

        addToast({
          type: 'success',
          description: data.uid
            ? 'Cupom alterado com sucesso!'
            : 'Cupom criado com sucesso!',
        });

        history.push('/coupons');

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, company, data, addToast, history, loadCoupons],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/coupons">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {id ? 'Editar Cupom' : 'Criar Cupom'}
                </h4>
                <div className="nk-block-des">
                  <p>Preencha o formulário abaixo e crie um novo cupom.</p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row g-2">
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">
                          Identificação do Cupom
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Código do Cupom</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Ex: CUPOM10"
                            defaultValue={data.code}
                            onChange={event => {
                              setData({ ...data, code: event.target.value });
                            }}
                          />
                          {errors.code && (
                            <span className="invalid">{errors.code}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Valor</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.value}
                            onChange={event => {
                              setData({
                                ...data,
                                value: parseFloat(event.target.value),
                              });
                            }}
                          />
                          {errors.value && (
                            <span className="invalid">{errors.value}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <div className="form-group mb-3">
                        <label className="form-label" htmlFor="category">
                          Tipo de desconto
                        </label>
                        <div className="form-control-wrap ">
                          <div className="form-control-select">
                            <select
                              className="form-control"
                              id="category"
                              value={data.type}
                              onChange={event => {
                                setData({
                                  ...data,
                                  type: parseInt(event.target.value),
                                });
                              }}
                            >
                              <option value="0">%</option>
                              <option value="1">R$</option>
                            </select>
                            {errors.type && (
                              <span className="invalid">{errors.type}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Valor Mínimo do pedido
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.min_value}
                            onChange={event => {
                              setData({
                                ...data,
                                min_value: parseFloat(event.target.value),
                              });
                            }}
                          />
                          {errors.min_value && (
                            <span className="invalid">{errors.min_value}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Valor Máximo do desconto
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.max_value}
                            onChange={event => {
                              setData({
                                ...data,
                                max_value: parseFloat(event.target.value),
                              });
                            }}
                          />
                          {errors.max_value && (
                            <span className="invalid">{errors.max_value}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Data Inicial</label>
                        <div className="form-control-wrap">
                          <input
                            id="date"
                            type="datetime-local"
                            className="form-control"
                            defaultValue={data.initDate}
                            onChange={event => {
                              setData({
                                ...data,
                                initDate: event.target.value,
                              });
                            }}
                          />
                          {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Data Final</label>
                        <div className="form-control-wrap">
                          <input
                            id="date"
                            type="datetime-local"
                            className="form-control"
                            defaultValue={data.endDate}
                            onChange={event => {
                              setData({
                                ...data,
                                endDate: event.target.value,
                              });
                            }}
                          />
                          {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Quantidade máxima de utilizações
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step=".01"
                            className="form-control"
                            defaultValue={data.usage_limit}
                            onChange={event => {
                              setData({
                                ...data,
                                usage_limit: parseInt(event.target.value),
                              });
                            }}
                          />
                          {errors.usage_limit && (
                            <span className="invalid">
                              {errors.usage_limit}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">
                          Limitar por pessoa?
                        </label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="active"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.phone_limit}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    phone_limit: !data.phone_limit,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Status?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="status"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.status}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    status: !data.status,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="status"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default CouponForm;
